import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import axios from 'axios';
import { slugify } from '../data/topics';
import { 
  Building2, Cpu, Lightbulb, Factory, Zap, Wifi, Sprout, Pill, Shield, Car, FileText, MessageSquare 
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EngageWithUs = () => {
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    department: '',
    service: '',
    message: '',
    phone: ''
  });

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.department) {
      toast.error('Please select a department.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/service-requests`, formData);
      toast.success(res.data.message);
      setFormData({
        name: '',
        email: '',
        company: '',
        department: '',
        service: '',
        message: '',
        phone: ''
      });
    } catch (err) {
      toast.error('Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const services = {
    consulting: [
      {
        title: 'Strategic Management Consulting',
        icon: <Building2 className="w-8 h-8" />,
        services: ['ESG Integration', 'Policy Advocacy', 'Market Entry'],
        caseStudy: 'Ministry of Energy Solar Framework',
        color: '#00CED1',
        image: 'https://images.unsplash.com/photo-1568952433726-3896e3881c65?q=85&w=800'
      },
      {
        title: 'Project Leadership',
        icon: <Lightbulb className="w-8 h-8" />,
        services: ['Infrastructure EPC', 'Tech Deployment', 'Supply Chain'],
        caseStudy: '250K Solar-Powered Homes Project',
        color: '#FF8C00',
        image: 'https://images.unsplash.com/photo-1496065187959-7f07b8353c55?q=85&w=800'
      },
      {
        title: 'Capital Solutions',
        icon: <FileText className="w-8 h-8" />,
        services: ['Venture Funding', 'Private Equity', 'Project Finance'],
        caseStudy: '$20M Rise Scale-Up Fund',
        color: '#00CED1',
        image: 'https://images.unsplash.com/photo-1644088379091-d574269d422f?q=85&w=800'
      }
    ],
    commodities: {
      minerals: ['Lithium', 'Gold', 'Platinum', 'Chrome', 'Nickel'],
      agriproducts: ['Non-GMO Maize', 'Sunflower', 'Tobacco', 'Coffee', 'Tea'],
      medical: ['PPE', 'Pharmaceuticals', 'Medical Equipment', 'Diagnostic Tools'],
      military: ['Security Equipment', 'Tactical Gear', 'Communication Systems']
    },
    tech: [
      {
        category: 'Automotive',
        icon: <Car className="w-6 h-6" />,
        solution: 'EV Charging Networks',
        description: 'Pan-African electric vehicle infrastructure',
        image: 'https://images.unsplash.com/photo-1609619385076-36a873425636?q=85&w=800'
      },
      {
        category: 'Aeronautical',
        icon: <Zap className="w-6 h-6" />,
        solution: 'Drone Surveillance Systems',
        description: 'Advanced aerial monitoring solutions',
        image: 'https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?q=85&w=800'
      },
      {
        category: 'Renewable',
        icon: <Factory className="w-6 h-6" />,
        solution: 'Solar Microgrids',
        description: 'Decentralized clean energy systems',
        image: 'https://images.unsplash.com/photo-1496065187959-7f07b8353c55?q=85&w=800'
      },
      {
        category: 'ICT',
        icon: <Wifi className="w-6 h-6" />,
        solution: 'Blockchain Supply Chain',
        description: 'Transparent traceability platforms',
        image: 'https://images.unsplash.com/photo-1644325349124-d1756b79dd42?q=85&w=800'
      }
    ]
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-[400px] flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1600880292089-90a7e086ee0c?crop=entropy&cs=srgb&fm=jpg&q=85')`,
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-[#FF8C00]/90 via-[#FF8C00]/70 to-transparent"></div>
          <div className="absolute inset-0" style={{
            background: 'radial-gradient(circle at 70% 30%, rgba(0, 206, 209, 0.2) 0%, transparent 50%)',
          }}></div>
        </div>
        <div className="relative z-10 max-w-6xl mx-auto px-6 text-white text-center">
          <h1 className="text-6xl font-bold mb-4" style={{ fontFamily: 'Montserrat, sans-serif' }}>
            Engage With Us
          </h1>
          <p className="text-2xl text-gray-200">Your Gateway to Africa's Sustainable Transformation</p>
        </div>
      </section>

      {/* Service Matrix */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold mb-6 text-gray-900" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              Our Services
            </h2>
            <div className="w-24 h-1 bg-[#00CED1] mx-auto mb-8"></div>
            <p className="text-xl text-gray-600">Comprehensive solutions for sustainable growth</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-16">
            {services.consulting.map((service, index) => (
              <Card 
                key={index} 
                className="hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 overflow-hidden"
              >
                <div className="relative h-48 overflow-hidden">
                  <img 
                    src={service.image} 
                    alt={service.title}
                    className="w-full h-full object-cover transition-transform duration-300 hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
                  <div className="absolute bottom-4 left-4 right-4">
                    <div 
                      className="inline-flex items-center gap-2 p-2 rounded-full backdrop-blur-sm" 
                      style={{ backgroundColor: `${service.color}40` }}
                    >
                      <div className="text-white">
                        {service.icon}
                      </div>
                    </div>
                  </div>
                </div>
                <div 
                  className="h-2" 
                  style={{ backgroundColor: service.color }}
                ></div>
                <CardHeader>
                  <h3 className="text-xl font-bold text-gray-900" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                    {service.title}
                  </h3>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3 mb-6">
                    {service.services.map((item, idx) => (
                      <div key={idx} className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full" style={{ backgroundColor: service.color }}></div>
                        <span className="text-gray-700">{item}</span>
                      </div>
                    ))}
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg mb-4">
                    <p className="text-sm text-gray-600 mb-1">Case Study:</p>
                    <p className="font-semibold text-gray-900">{service.caseStudy}</p>
                  </div>
                  <Button 
                    className="w-full bg-[#FF8C00] hover:bg-[#e07d00] text-white font-semibold transition-all duration-300 transform hover:scale-105"
                    onClick={() => {
                      handleInputChange('service', service.title);
                      document.getElementById('request-form').scrollIntoView({ behavior: 'smooth' });
                    }}
                  >
                    Request Proposal
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Commodity Trade */}
      <section className="py-20 px-6 bg-gradient-to-br from-gray-50 to-gray-100 relative overflow-hidden">
        {/* Decorative background elements */}
        <div className="absolute top-0 left-0 w-64 h-64 bg-[#00CED1]/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-[#FF8C00]/5 rounded-full blur-3xl"></div>
        <div className="absolute inset-0 opacity-5" style={{
          backgroundImage: 'radial-gradient(circle at 2px 2px, rgba(0, 206, 209, 0.15) 1px, transparent 0)',
          backgroundSize: '40px 40px'
        }}></div>
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold mb-6 text-gray-900" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              Commodity Trade
            </h2>
            <div className="w-24 h-1 bg-[#FF8C00] mx-auto mb-8"></div>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <Card className="border-2 border-[#00CED1] overflow-hidden">
              <div className="relative h-40 overflow-hidden">
                <img src="https://static.prod-images.emergentagent.com/jobs/0b23b238-d9a7-4a7a-8c18-f58cb151cea3/images/c917a7a152f716599624e3d2616939492ddc22bcaba7c8f08e81ac752b8666ea.png" alt="Mining operations" className="w-full h-full object-cover transition-transform duration-300 hover:scale-110" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#00CED1]/80 to-transparent"></div>
              </div>
              <CardHeader className="bg-[#00CED1] text-white">
                <h3 className="text-2xl font-bold flex items-center gap-2">
                  <Sprout className="w-6 h-6" />
                  Minerals
                </h3>
              </CardHeader>
              <CardContent className="p-6">
                <div className="flex flex-wrap gap-2">
                  {services.commodities.minerals.map((item, index) => (
                    <Link key={index} to={`/explore/${slugify(item)}`} data-testid={`commodity-link-${slugify(item)}`}>
                      <Badge 
                        className="bg-[#00CED1]/10 text-[#00CED1] border border-[#00CED1] hover:bg-[#00CED1] hover:text-white cursor-pointer transition-all duration-200"
                      >
                        {item}
                      </Badge>
                    </Link>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card className="border-2 border-[#FF8C00] overflow-hidden">
              <div className="relative h-40 overflow-hidden">
                <img src="https://images.unsplash.com/photo-1500937386664-56d1dfef3854?q=85&w=800" alt="Agriculture harvest" className="w-full h-full object-cover transition-transform duration-300 hover:scale-110" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#FF8C00]/80 to-transparent"></div>
              </div>
              <CardHeader className="bg-[#FF8C00] text-white">
                <h3 className="text-2xl font-bold flex items-center gap-2">
                  <Sprout className="w-6 h-6" />
                  Agriproducts
                </h3>
              </CardHeader>
              <CardContent className="p-6">
                <div className="flex flex-wrap gap-2">
                  {services.commodities.agriproducts.map((item, index) => (
                    <Link key={index} to={`/explore/${slugify(item)}`} data-testid={`commodity-link-${slugify(item)}`}>
                      <Badge 
                        className="bg-[#FF8C00]/10 text-[#FF8C00] border border-[#FF8C00] hover:bg-[#FF8C00] hover:text-white cursor-pointer transition-all duration-200"
                      >
                        {item}
                      </Badge>
                    </Link>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card className="border-2 border-[#00CED1] overflow-hidden">
              <div className="relative h-40 overflow-hidden">
                <img src="https://images.unsplash.com/photo-1584634731339-252c581abfc5?q=85&w=800" alt="Medical supplies" className="w-full h-full object-cover transition-transform duration-300 hover:scale-110" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#00CED1]/80 to-transparent"></div>
              </div>
              <CardHeader className="bg-[#00CED1] text-white">
                <h3 className="text-2xl font-bold flex items-center gap-2">
                  <Pill className="w-6 h-6" />
                  Medical
                </h3>
              </CardHeader>
              <CardContent className="p-6">
                <div className="flex flex-wrap gap-2">
                  {services.commodities.medical.map((item, index) => (
                    <Link key={index} to={`/explore/${slugify(item)}`} data-testid={`commodity-link-${slugify(item)}`}>
                      <Badge 
                        className="bg-[#00CED1]/10 text-[#00CED1] border border-[#00CED1] hover:bg-[#00CED1] hover:text-white cursor-pointer transition-all duration-200"
                      >
                        {item}
                      </Badge>
                    </Link>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card className="border-2 border-[#FF8C00] overflow-hidden">
              <div className="relative h-40 overflow-hidden">
                <img src="https://images.unsplash.com/photo-1557597774-9d273605dfa9?q=85&w=800" alt="Security systems" className="w-full h-full object-cover transition-transform duration-300 hover:scale-110" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#FF8C00]/80 to-transparent"></div>
              </div>
              <CardHeader className="bg-[#FF8C00] text-white">
                <h3 className="text-2xl font-bold flex items-center gap-2">
                  <Shield className="w-6 h-6" />
                  Military
                </h3>
              </CardHeader>
              <CardContent className="p-6">
                <div className="flex flex-wrap gap-2">
                  {services.commodities.military.map((item, index) => (
                    <Link key={index} to={`/explore/${slugify(item)}`} data-testid={`commodity-link-${slugify(item)}`}>
                      <Badge 
                        className="bg-[#FF8C00]/10 text-[#FF8C00] border border-[#FF8C00] hover:bg-[#FF8C00] hover:text-white cursor-pointer transition-all duration-200"
                      >
                        {item}
                      </Badge>
                    </Link>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Tech Innovations */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold mb-6 text-gray-900" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              Technology Solutions
            </h2>
            <div className="w-24 h-1 bg-[#00CED1] mx-auto mb-8"></div>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {services.tech.map((tech, index) => (
              <Card 
                key={index} 
                className="hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 overflow-hidden"
              >
                <div className="relative h-56 overflow-hidden">
                  <img 
                    src={tech.image} 
                    alt={tech.solution}
                    className="w-full h-full object-cover transition-transform duration-300 hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent"></div>
                  <div className="absolute bottom-4 left-4 right-4">
                    <Badge className="mb-3 bg-[#FF8C00] hover:bg-[#e07d00]">{tech.category}</Badge>
                    <div className="flex items-start gap-3">
                      <div className="p-2 bg-[#00CED1]/80 backdrop-blur-sm rounded-full text-white">
                        {tech.icon}
                      </div>
                      <div className="flex-1">
                        <h3 className="text-xl font-bold text-white mb-1">{tech.solution}</h3>
                        <p className="text-gray-200 text-sm">{tech.description}</p>
                      </div>
                    </div>
                  </div>
                </div>
                <CardContent className="p-6">
                  <Button 
                    className="w-full bg-[#FF8C00] hover:bg-[#e07d00] text-white font-semibold transition-all duration-300"
                    onClick={() => {
                      handleInputChange('service', tech.solution);
                      document.getElementById('request-form').scrollIntoView({ behavior: 'smooth' });
                    }}
                  >
                    Learn More
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Service Request Form */}
      <section id="request-form" className="py-20 px-6 bg-gradient-to-br from-gray-900 to-gray-800 relative overflow-hidden">
        {/* Animated background elements */}
        <div className="absolute top-1/4 right-0 w-96 h-96 bg-[#00CED1]/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-1/4 left-0 w-80 h-80 bg-[#FF8C00]/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
        <div className="absolute inset-0 opacity-5" style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1739300293504-234817eead52?q=85&w=800')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}></div>
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <MessageSquare className="w-16 h-16 text-[#00CED1] mx-auto mb-4" />
            <h2 className="text-5xl font-bold mb-6 text-white" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              Request Service
            </h2>
            <div className="w-24 h-1 bg-[#00CED1] mx-auto mb-8"></div>
            <p className="text-xl text-gray-300">Our team will respond within 24 hours</p>
          </div>

          <Card className="bg-white/5 backdrop-blur-lg border-gray-700">
            <CardContent className="p-8">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="name" className="text-white">Full Name *</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => handleInputChange('name', e.target.value)}
                      required
                      className="bg-white/10 border-gray-600 text-white placeholder:text-gray-400"
                      placeholder="Your name"
                    />
                  </div>

                  <div>
                    <Label htmlFor="email" className="text-white">Email *</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      required
                      className="bg-white/10 border-gray-600 text-white placeholder:text-gray-400"
                      placeholder="your@email.com"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="company" className="text-white">Company</Label>
                    <Input
                      id="company"
                      value={formData.company}
                      onChange={(e) => handleInputChange('company', e.target.value)}
                      className="bg-white/10 border-gray-600 text-white placeholder:text-gray-400"
                      placeholder="Your company"
                    />
                  </div>

                  <div>
                    <Label htmlFor="phone" className="text-white">Phone</Label>
                    <Input
                      id="phone"
                      value={formData.phone}
                      onChange={(e) => handleInputChange('phone', e.target.value)}
                      className="bg-white/10 border-gray-600 text-white placeholder:text-gray-400"
                      placeholder="+263..."
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="department" className="text-white">Department *</Label>
                    <Select value={formData.department} onValueChange={(value) => handleInputChange('department', value)}>
                      <SelectTrigger className="bg-white/10 border-gray-600 text-white">
                        <SelectValue placeholder="Select department" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Energy">Energy</SelectItem>
                        <SelectItem value="Commodities">Commodities</SelectItem>
                        <SelectItem value="Technology">Technology</SelectItem>
                        <SelectItem value="Capital">Capital Solutions</SelectItem>
                        <SelectItem value="Consulting">Strategic Advisory</SelectItem>
                        <SelectItem value="Health">Health & Wellness</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="service" className="text-white">Service Interest</Label>
                    <Input
                      id="service"
                      value={formData.service}
                      onChange={(e) => handleInputChange('service', e.target.value)}
                      className="bg-white/10 border-gray-600 text-white placeholder:text-gray-400"
                      placeholder="Specific service"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="message" className="text-white">Message *</Label>
                  <Textarea
                    id="message"
                    value={formData.message}
                    onChange={(e) => handleInputChange('message', e.target.value)}
                    required
                    rows={5}
                    className="bg-white/10 border-gray-600 text-white placeholder:text-gray-400"
                    placeholder="Tell us about your project or requirements..."
                  />
                </div>

                <Button 
                  type="submit" 
                  size="lg" 
                  disabled={submitting}
                  data-testid="service-request-submit-button"
                  className="w-full bg-[#FF8C00] hover:bg-[#e07d00] text-white font-bold text-lg py-6 transition-all duration-300 transform hover:scale-105"
                >
                  {submitting ? 'Submitting...' : 'Submit Request'}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default EngageWithUs;